import { setUserIconColor, showModal, StatBubble } from 'components/AppShell';
import { Icon } from 'components/Icon';
import { signOut } from 'components/Login';
import { appendChild, type JSXChild, render } from 'engine/index';
import { Hourglass, RefreshCcw, Trophy, X } from 'lucide-static';
import type { PlayAndConnectCard, PlayAndConnectUpdate } from 'shared/modelTypes/play-and-connect';
import { lateinit, unreachable } from 'shared/util';
import { Game } from './game';
import { rpc, State } from 'engine/rpc';
import { Timer } from 'components/Timer';
import { marked } from 'marked';

export class PlayAndConnect extends Game<'play-and-connect-spel'> {
	override maxPlayers: number = State.numberOfPlayers || 12;

	playandconnectEl: HTMLElement = lateinit;
	scoreEl: HTMLElement = lateinit;
	view: HTMLElement = lateinit;

	closeModal?: () => void;

	override getGameName(): string {
		return 'Play and Connect';
	}

	override defineBottomUI(): JSXChild {
		return <StatBubble color="green" label="Punten" value="0" id="score" />;
	}
	override onShellCreated(view: HTMLElement): void {
		super.onShellCreated(view);
		this.playandconnectEl = view.querySelector('#playandconnect-count')!;
		this.scoreEl = view.querySelector('#score')!;
		this.view = view;
	}

	override handleUpdate(update: PlayAndConnectUpdate): void {
		const sessionId = State.gameId.toString();
		const playerId = State.playerId;
		const kind = update.kind;
		// const playerId = State.playerId;

		console.log('message', update);

		switch (kind) {
			case 'trigger-effect':
				this.triggerEffect(update);
				break;
			case 'show-login-screen':
				this.showLoginScreen();
				break;
			case 'show-register-screen':
				if (typeof update.numberOfPlayers === 'number') {
					State.numberOfPlayers = update.numberOfPlayers;
				}
				this.maxPlayers = State.numberOfPlayers;
				this.showRegisterScreen('player', undefined, true, true);
				break;
			case 'update-players-state':
				this.updatePlayersState(update);
				break;
			case 'update-player-state':
				State.name = update.player.name;
				State.color = update.player.color;
				State.score = update.player.score;

				this.shellNameEl.innerText = update.player.name;
				setUserIconColor(this.userIconEl, update.player.color);
				this.scoreEl.innerText = (State.score ?? 0).toString();
				break;
			case 'show-question-screen':
				{
					if (this.closeModal) {
						this.closeModal();
						this.closeModal = undefined;
					}

					render(this.shellContentEl, <QuestionScreen roundNumber={update.roundNumber} question={update.question} />);
				}
				break;
			case 'show-swap-screen':
				if (this.closeModal) this.closeModal();
				render(
					this.shellContentEl,
					<SwapScreen
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
						roundNumber={update.roundNumber}
						text={update.text}
						question={update.question}
						questionNumber={update.questionNumber}
					/>,
				);
				break;
			case 'show-select-player-screen':
				{
					const selectPlayerModel = (chosenPlayerId: string) => {
						if (this.closeModal) this.closeModal();

						showModal(
							(confirm) => {
								this.closeModal = confirm;
								return (
									<div class="rounded-md bg-white p-4 shadow-md">
										<h2 class="mb-4 text-lg font-bold">Vat jullie gesprek kort samen</h2>
										<p>Gebruik steekwoorden om jullie gesprek over "{update.question}" kort samen te vatten.</p>
										<form
											on:submit={(e: SubmitEvent) => {
												e.preventDefault();
												const form = e.target as HTMLFormElement;
												const formData = new FormData(form);
												const answerText = (formData.get('details') as string) || '';

												rpc.sendPlayAndConnectMessage.query({
													sessionId,
													update: {
														kind: 'send-answers',
														playerId,
														chosenPlayerId,
														answer: answerText,
													},
												});
												confirm();
											}}
										>
											<div class="py-6">
												<label class="font-semibold text-gray-800">Samenvatting:</label>
												<textarea
													name="details"
													rows="5"
													placeholder="Beschrijving"
													required
													class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
													on:change={(e: Event) => {
														const textarea = e.target as HTMLTextAreaElement;
														const answerText = textarea.value;

														rpc.sendPlayAndConnectMessage.query({
															sessionId,
															update: {
																kind: 'send-answers',
																playerId,
																chosenPlayerId,
																answer: answerText,
															},
														});
													}}
												/>
											</div>
											<div class="flex items-center justify-between">
												<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
													Insturen
												</button>
												<InlineTimer timerFrom={update.timerFrom} timerTo={update.timerTo} />
											</div>
										</form>
									</div>
								);
							},
							{ passiveDismiss: true },
						);
					};
					render(
						this.shellContentEl,
						<SelectPlayerScreen
							roundNumber={update.roundNumber}
							question={update.question}
							players={update.players}
							selectPlayerModel={selectPlayerModel}
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
						/>,
					);
				}
				break;
			case 'show-connect-players-screen':
				{
					const selectTwoPlayersModel = (chosenPlayerId: string, secondChosenPlayerId?: string) => {
						if (!secondChosenPlayerId) return;
						if (this.closeModal) this.closeModal();

						showModal(
							(confirm) => {
								this.closeModal = confirm;
								return (
									<div class="rounded-md bg-white p-4 shadow-md">
										<h2 class="mb-4 text-lg font-bold">Vat jullie koppeling kort samen</h2>
										<p>
											Gebruik steekwoorden om uit te leggen waarom deze twee spelers interessant zijn om met elkaar te
											praten
										</p>
										<form
											on:submit={(e: SubmitEvent) => {
												e.preventDefault();
												const form = e.target as HTMLFormElement;
												const formData = new FormData(form);
												const answerText = (formData.get('details') as string) || '';

												rpc.sendPlayAndConnectMessage.query({
													sessionId,
													update: {
														kind: 'send-final-answers',
														playerId,
														chosenPlayerId,
														secondChosenPlayerId,
														answer: answerText,
													},
												});
												confirm();
											}}
										>
											<div class="py-6">
												<label class="font-semibold text-gray-800">Waarom deze koppeling?</label>
												<textarea
													name="details"
													rows="5"
													placeholder="Bijv: Beide houden van fotografie en reizen..."
													required
													class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
												/>
											</div>
											<div class="flex items-center justify-between">
												<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
													Insturen
												</button>
												<InlineTimer timerFrom={update.timerFrom} timerTo={update.timerTo} />
											</div>
										</form>
									</div>
								);
							},
							{ passiveDismiss: true },
						);
					};

					render(
						this.shellContentEl,
						<SelectPlayerScreen
							roundNumber={update.roundNumber}
							question={update.question}
							players={update.players}
							selectPlayerModel={selectTwoPlayersModel}
							timerFrom={update.timerFrom}
							timerTo={update.timerTo}
							isFinalQuestion={true}
						/>,
					);
				}
				break;
			case 'show-share-contact':
				render(
					this.shellContentEl,
					<ShareContactScreen
						timerFrom={update.timerFrom}
						timerTo={update.timerTo}
						onSend={() =>
							render(this.shellContentEl, <WaitScreen message="Wacht tot de timer is afgelopen om de eindstand te zien." />)
						}
					/>,
				);
				break;
			case 'show-results-screen':
				render(this.shellContentEl, <ResultsScreen results={update.players} />);
				break;
			case 'show-message':
				appendChild(this.view, <ShowMessage head={update.head} body={update.body} />);
				break;
			case 'show-wait-screen':
				render(
					this.shellContentEl,
					<WaitScreen
						message={update.message}
						nextQuestion={update.nextQuestion}
						showMakeAnotherConnection={update.showMakeAnotherConnection}
					/>,
				);
				break;

			case 'show-stop-screen':
				render(
					this.shellContentEl,
					<StopScreen
						message={update.message}
						nextQuestion={update.nextQuestion}
						nextQuestionNumber={update.nextQuestionNumber}
					/>,
				);
				break;
			case 'show-qualitypicker-screen':
				{
					if (this.closeModal) {
						this.closeModal();
						this.closeModal = undefined;
					}

					render(
						this.shellContentEl,
						<QualityPickerScreen
							title={update.title}
							message={update.message}
							cards={update.cards}
							updateSelection={(selected) => {
								rpc.sendPlayAndConnectMessage.query({
									sessionId,
									update: {
										kind: 'update-relevant-cards',
										playerId,
										relevantCards: selected,
									},
								});
							}}
						/>,
					);
				}
				break;
			case 'display-trade-offer':
				// close previous modal
				if (this.closeModal) this.closeModal();
				showModal((confirm) => {
					this.closeModal = confirm;
					return (
						<div class="rounded-md bg-white p-4 shadow-lg">
							<h2>
								<strong>{update.player.name}</strong> schenkt jou het volgende kaartje: <strong>{update.card}</strong>
							</h2>
							<small>Kies een kaart om weg te geven.</small>
							<div class="flex flex-col gap-2">
								{State.kwaliteitenCards.map((c) => (
									<button
										on:click={() => {
											rpc.sendPlayAndConnectMessage.query({
												sessionId,
												update: {
													kind: 'finalize-trade',
													playerId,
													otherPlayerColor: update.player.color,
													giveCard: c.name,
												},
											});
											confirm();
										}}
										class="border border-gray-300 bg-gray-50 px-3 py-2 font-semibold"
									>
										{c.name}
									</button>
								))}
							</div>
							<button
								on:click={() => {
									rpc.sendPlayAndConnectMessage.query({
										sessionId,
										update: {
											kind: 'finalize-trade',
											playerId,
											otherPlayerColor: update.player.color,
											giveCard: '',
										},
									});
									confirm();
								}}
								class="mx-auto mt-3 bg-theme-600 px-3 py-2 font-semibold text-theme-50"
							>
								Annuleren
							</button>
						</div>
					);
				});

				break;
			case 'display-purchase-request':
				// close previous modal
				if (this.closeModal) this.closeModal();
				showModal((confirm) => {
					this.closeModal = confirm;
					return (
						<div class="rounded-md bg-white p-4 shadow-lg">
							<h2>
								<strong>{update.player.name}</strong> will een kaart van jou kopen.
							</h2>
							<small>
								Selecteer de kaart die zij willen hebben <em>of</em> de kat in de zak. Je mag niet weigeren.
							</small>
							<div class="flex flex-col gap-2">
								{State.kwaliteitenCards.map((c) => (
									<button
										on:click={() => {
											rpc.sendPlayAndConnectMessage.query({
												sessionId,
												update: {
													kind: 'finalize-purchase',
													playerId,
													otherPlayerColor: update.player.color,
													giveCard: c.name,
												},
											});
											confirm();
										}}
										class="border border-gray-300 bg-gray-50 px-3 py-2 font-semibold"
									>
										{c.name}
									</button>
								))}
							</div>
						</div>
					);
				});
				break;
			case 'finalized-purchase':
				if (this.closeModal) {
					this.closeModal();
				}
				break;
			case 'update-player-contact':
				break;
			default:
				unreachable(kind);
		}
	}

	override updatePlayersState(update: {
		kind: 'update-players-state';
		players: { name: string; color: string }[];
		numberOfPlayers?: number;
	}) {
		if (typeof update.numberOfPlayers === 'number' && update.numberOfPlayers > 0) {
			State.numberOfPlayers = update.numberOfPlayers;
			this.maxPlayers = update.numberOfPlayers;
		}
		super.updatePlayersState(update);
	}
}

function WaitScreen({
	message,
	nextQuestion,
	showMakeAnotherConnection,
}: {
	message: string;
	nextQuestion?: string;
	showMakeAnotherConnection?: boolean;
}) {
	const sessionId = State.gameId.toString();
	const playerId = State.playerId;

	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-md">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			<div class="text-center text-xl text-gray-800">
				<span class="font-semibold text-gray-900">{message}</span>
			</div>
			{nextQuestion && (
				<div class="mt-4 text-center text-lg text-gray-600">
					<span class="font-semibold">Volgende vraag: </span>
					<span>{nextQuestion}</span>
				</div>
			)}
			{showMakeAnotherConnection && (
				<button
					class="mt-6 rounded-md bg-theme-600 px-6 py-3 text-theme-50"
					on:click={() => {
						rpc.sendPlayAndConnectMessage.query({
							sessionId,
							update: {
								kind: 'restart-final-question',
								playerId,
							},
						});
					}}
				>
					Maak nog een koppeling
				</button>
			)}
		</div>
	);
}

function StopScreen({
	message,
	nextQuestion,
	nextQuestionNumber,
}: {
	message: string;
	nextQuestion?: string;
	nextQuestionNumber?: number;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-lg">
			<Icon icon={Hourglass} class="size-20 text-gray-300" />
			{nextQuestion && (
				<div class="mt-4 text-center text-lg text-gray-600">
					{nextQuestionNumber ? <span class="text-4xl font-bold text-gray-900">Vraag {nextQuestionNumber}: </span> : null}
					<span class="text-4xl">{nextQuestion}</span>
				</div>
			)}
			<div class="text-center text-xl text-gray-800">
				<span class="font-semibold text-gray-800">{message}</span>
			</div>
		</div>
	);
}

export function SwapScreen({
	roundNumber,
	text,
	timerFrom,
	timerTo,
	question,
	questionNumber,
}: {
	timerFrom: number;
	timerTo: number;
	roundNumber: number;
	text?: string;
	question?: string;
	questionNumber?: number;
}) {
	return (
		<div class="flex w-full flex-col items-center justify-center gap-2 p-6 md:max-w-screen-lg">
			<Timer timerFrom={timerFrom} timerTo={timerTo} />
			{question && questionNumber && (
				<div class="mt-4 text-center text-lg text-gray-600">
					<span class="text-4xl font-bold text-gray-900">Vraag {questionNumber}: </span>
					<span class="text-4xl">{question}</span>
				</div>
			)}
			{!question && <h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">Vraag {roundNumber}:</h2>}
			{text && (
				<div class="text-center text-xl text-gray-800">
					<p>{text}</p>
				</div>
			)}
			<Icon icon={RefreshCcw} class="size-20 text-gray-300" />
		</div>
	);
}

export function QuestionScreen({ roundNumber, question }: { roundNumber: number; question: string }) {
	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">Vraag {roundNumber}:</h2>
			<pre
				class="justify-items: center; w-full whitespace-normal text-center font-sans text-gray-800"
				__innerHTML={marked(question)}
			></pre>
		</div>
	);
}

export function SelectPlayerScreen({
	roundNumber,
	question,
	players,
	selectPlayerModel,
	timerFrom,
	timerTo,
	isFinalQuestion = false,
}: {
	roundNumber: number;
	question: string;
	players: { id: string; color: string; name: string }[];
	selectPlayerModel: (chosenPlayerId: string, secondChosenPlayerId?: string) => void;
	timerFrom: number;
	timerTo: number;
	isFinalQuestion?: boolean;
}) {
	const otherPlayers = isFinalQuestion ? players : players.filter((p) => p.id !== State.playerId);

	let selectedPlayer1: string | null = null;
	let selectedPlayer2: string | null = null;

	const instructionEl: HTMLElement = (
		<p class="mb-4 text-lg font-semibold text-gray-700">{isFinalQuestion ? 'Selecteer een eerste speler' : 'Selecteer een speler'}</p>
	);

	function updateInstruction() {
		if (!isFinalQuestion) return;

		if (!selectedPlayer1) {
			instructionEl.textContent = 'Selecteer een eerste speler';
		} else if (!selectedPlayer2) {
			instructionEl.textContent = 'Selecteer een tweede speler';
		}
	}

	function resetSelections() {
		selectedPlayer1 = null;
		selectedPlayer2 = null;
		const allPlayers = document.querySelectorAll('[data-selected], [data-active]');
		allPlayers.forEach((el) => {
			el.removeAttribute('data-selected');
			el.removeAttribute('data-active');
		});
		updateInstruction();
	}

	resetSelections();

	return (
		<div class="flex h-[90vh] flex-col items-center gap-2 p-6">
			<div class="flex flex-col items-center">
				<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">Vraag {roundNumber}:</h2>

				<pre class="w-full whitespace-normal text-center font-sans text-gray-800" __innerHTML={marked(question)} />

				<Timer timerFrom={timerFrom} timerTo={timerTo} alwaysShowSeconds={true} />

				{instructionEl}
			</div>

			<div class="w-full flex-1 overflow-auto">
				<div class="grid grid-cols-3 gap-3 lg:grid-cols-3">
					{otherPlayers.map((p) => {
						const playerEl: HTMLElement = (
							<div class="cursor-pointer rounded-md p-2 transition-all data-[selected]:opacity-50">
								<Player
									player={p}
									playerNumber={Number(p.color)}
									onSelect={(id) => {
										if (!isFinalQuestion) {
											selectPlayerModel(id);
											return;
										}

										if (id === selectedPlayer1 || id === selectedPlayer2) return;

										if (!selectedPlayer1) {
											selectedPlayer1 = id;
											playerEl.setAttribute('data-active', '');
											playerEl.setAttribute('data-selected', '');
											updateInstruction();
											return;
										}

										if (!selectedPlayer2) {
											selectedPlayer2 = id;
											playerEl.setAttribute('data-active', '');
											playerEl.setAttribute('data-selected', '');
											updateInstruction();

											setTimeout(() => {
												selectPlayerModel(selectedPlayer1!, selectedPlayer2!);
												resetSelections();
											}, 150);
										}
									}}
								/>
							</div>
						);

						return playerEl;
					})}
				</div>
			</div>
		</div>
	);
}

export function ShareContactScreen({ timerFrom, timerTo, onSend }: { timerFrom: number; timerTo: number; onSend: () => void }) {
	const sessionId = State.gameId.toString();
	const playerId = State.playerId;

	return (
		<div class="flex h-[90vh] flex-col items-center gap-2 p-6">
			<div class="flex flex-col items-center">
				<Timer timerFrom={timerFrom} timerTo={timerTo} alwaysShowSeconds={true} />

				<p class="mb-4 text-lg font-semibold text-gray-700">Wil je jouw contactgegevens delen met de groep?</p>

				<form
					on:submit={(e: SubmitEvent) => {
						e.preventDefault();

						const form = e.currentTarget as HTMLFormElement;
						const formData = new FormData(form);
						const contactDetails = (formData.get('contact-details') as string) || '';
						const allowShare = Boolean(formData.get('allow-share'));

						rpc.sendPlayAndConnectMessage.query({
							sessionId,
							update: {
								kind: 'update-player-contact',
								playerId,
								contact: contactDetails,
								shareContact: allowShare,
							},
						});

						onSend();
					}}
				>
					<div class="py-6">
						<label class="font-semibold text-gray-800">Contactgegevens:</label>
						<textarea
							name="contact-details"
							rows={5}
							placeholder="Telefoonnummer of emailadres"
							required
							class="block w-full rounded-md border border-gray-200 bg-gray-50 p-2"
						/>
						<div class="flex items-center gap-2">
							<input type="checkbox" id="allow-share" name="allow-share" class="size-4" />

							<label for="allow-share" class="text-sm text-gray-700">
								Ik ga akkoord met het delen van mijn contactgegevens
							</label>
						</div>
					</div>

					<div class="flex justify-center">
						<button type="submit" class="rounded-md bg-theme-600 p-3 text-theme-50">
							Insturen
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
export function QualityPickerScreen({
	title,
	message,
	cards,
	updateSelection,
}: {
	title: string;
	message: string;
	cards: PlayAndConnectCard[];
	updateSelection: (selected: string[]) => void;
}) {
	const selected: Set<string> = new Set();

	return (
		<div class="flex h-fit max-w-full shrink-0 flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="mb-2 text-3xl font-bold text-gray-900 sm:mb-6 sm:text-6xl">{title}</h2>
			<pre class="w-full whitespace-normal text-left font-sans text-gray-800">{message}</pre>
			<Timer timerFrom={Date.now()} timerTo={Date.now() + 600} />

			<div class="grid grid-cols-2 gap-3 lg:grid-cols-3">
				{cards.map((c) => {
					const view: HTMLElement = (
						<div class="cursor-pointer rounded-md opacity-70 transition-transform duration-150 hover:scale-[101%] hover:opacity-100 hover:shadow-md data-[selected]:scale-[102%] data-[selected]:opacity-100 data-[selected]:shadow-sm data-[selected]:outline data-[selected]:outline-theme-900">
							<cards
								card={c}
								onCardTap={() => {
									view.toggleAttribute('data-selected');
									if (view.hasAttribute('data-selected')) {
										selected.add(c.name);
									} else {
										selected.delete(c.name);
									}
									updateSelection([...selected]);
								}}
							/>
						</div>
					);
					return view;
				})}
			</div>
		</div>
	);
}

export function ResultsScreen({ results }: { results: { name: string; color: string; score: number }[] }) {
	return (
		<div class="flex w-full flex-col items-center gap-2 p-6 md:max-w-screen-md">
			<h2 class="text-2xl font-bold text-gray-900 lg:mb-6 lg:text-6xl">Einduitslag</h2>
			<table class="overflow-hidden rounded-md shadow-sm">
				<thead>
					<tr class="bg-gray-100">
						<th class="px-5 py-1">Rank</th>
						<th class="px-5 py-1">Pion</th>
						<th class="px-5 py-1">Naam</th>
						<th class="px-5 py-1">Punten</th>
					</tr>
				</thead>
				<tbody>
					{results.map((r, i) => {
						const highlight = State.color == r.color;

						return (
							<tr class={highlight ? 'bg-yellow-100 ring-2 ring-yellow-400' : 'even:bg-gray-50'}>
								<td class="px-5 py-1">
									<div class="inline-flex items-baseline gap-3">
										<span class="font-bold">{i + 1}</span>
										{i == 0 && <Icon icon={Trophy} class="size-4 text-orange-400" />}
										{i == 1 && <Icon icon={Trophy} class="size-4 text-gray-400" />}
										{i == 2 && <Icon icon={Trophy} class="size-4 text-orange-700" />}
										{i >= 3 && <Icon icon={Trophy} class="size-4 opacity-0" />}
									</div>
								</td>
								<td class="px-5 py-1">{__(['player ' + r.color])}</td>
								<td class="px-5 py-1">{r.name}</td>
								<td class="px-5 py-1">{r.score}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			<div class="flex gap-3">
				<button
					class="rounded-md bg-theme-600 p-4 text-theme-50"
					on:click={async () => {
						const res = await rpc.getReportForSession.query({ gameId: State.gameId, sessionId: State.sessionId });
						if (!('success' in res) || !res.success) {
							console.error(res.error);
							return;
						}
						const path = res.reportUrl;
						const url = new URL(path, window.location.origin);
						const link = document.createElement('a');
						link.href = url.toString();
						link.download = path.split('/').pop() || 'download';
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					}}
				>
					Download rapport
				</button>
				<button class="rounded-md bg-gray-200 p-4 text-gray-900" on:click={signOut}>
					Spel afsluiten
				</button>
			</div>
		</div>
	);
}

function Player({
	player,
	playerNumber,
	onSelect,
}: {
	player: { id: string; name: string; color: string };
	playerNumber: number;
	onSelect: (id: string) => void;
	isFinalQuestion?: boolean;
}) {
	return (
		<div on:click={() => onSelect(player.id)} class="flex flex-col items-center">
			<div class="flex items-center justify-center p-1">
				<div
					class="flex size-20 min-h-20 min-w-20 items-center justify-center rounded-full bg-theme-500 text-theme-50"
					style={{ backgroundColor: player.color }}
				>
					<span class="text-3xl font-bold leading-none">{playerNumber}</span>
				</div>
			</div>
			<span class="mt-1 text-center">{player.name}</span>
		</div>
	);
}

function InlineTimer({ timerFrom, timerTo }: { timerFrom: number; timerTo: number }) {
	const timeEl: HTMLElement = <span class="font-mono font-semibold text-gray-700">--:--</span>;

	const update = () => {
		const remaining = Math.max(0, Math.ceil((timerTo - Date.now()) / 1000));
		const mins = Math.floor(remaining / 60);
		const secs = remaining % 60;
		timeEl.textContent = `${mins}:${String(secs).padStart(2, '0')}`;
		if (remaining > 0) requestAnimationFrame(update);
	};

	requestAnimationFrame(update);

	return (
		<div class="flex items-center gap-1 text-sm text-gray-500">
			<span>Tijd om in te sturen:</span>
			{timeEl}
		</div>
	);
}

export function ShowMessage({ head, body }: { head: string; body: string }) {
	const view = (
		<div
			popup-message
			class="absolute bottom-10 left-1/2 flex w-4/5 -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
		>
			<button
				action-cancel
				class="absolute right-2 top-2 rounded-md p-2"
				on:click={(event: MouseEvent) => {
					const btn = event.target as HTMLElement;
					const closePopUp = btn.closest('[popup-message]');
					if (closePopUp) {
						closePopUp.remove();
					}
				}}
			>
				<Icon icon={X} class="size-6" />
			</button>
			<h3 class="mb mb-2 text-lg font-bold text-gray-900">{head}</h3>
			<p class="w-full p-1 text-center">{body}</p>
		</div>
	);

	return view;
}
